import { Data } from '.generated/Foundation.CW.model';
import { Field, useSitecore } from '@sitecore-content-sdk/nextjs';
import { withStandardComponentWrapper } from 'helpers/HOC';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { convert24hToAmPm } from 'lib/utils/time';
import React from 'react';

type OfficeHoursItem = {
  fields?: {
    officeHourType?: Data.Lookups.OfficeHoursType;
    open?: Field<string>;
    close?: Field<string>;
  };
  name?: string;
};

type HoursItem = {
  fields: {
    disclaimerLabel?: Field<string>;
    heading?: Field<string>;
    availableHours?: Array<OfficeHoursItem>;
  };
};

type HoursProps = {
  officeHoursTitle?: Field<string>;
  phoneHoursTitle?: Field<string>;
  officeHoursDisclaimerText?: Field<string>;
  phoneHoursDisclaimerText?: Field<string>;
  officeHours?: Array<OfficeHoursItem>;
  phoneHours?: Array<OfficeHoursItem>;
};

function getLinearOfficeHours(
  hours: { name?: string; day: string; openTime: string; closeTime: string }[]
) {
  if (!hours || hours.length === 0) return null;

  // Canonical week order and abbreviations
  const weekOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const weekAbbr = {
    Monday: 'Mon',
    Tuesday: 'Tues',
    Wednesday: 'Weds',
    Thursday: 'Thurs',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun',
  };
  const abbrToFull = {
    Mon: 'Monday',
    Tues: 'Tuesday',
    Tue: 'Tuesday',
    Weds: 'Wednesday',
    Wed: 'Wednesday',
    Thurs: 'Thursday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
  };

  // Normalize day names to full names
  const normalizedHours = hours.map((h) => ({
    ...h,
    day: abbrToFull[h.day as keyof typeof abbrToFull] || h.day,
  }));

  // Sort by canonical week order
  const sortedHours = [...normalizedHours].sort(
    (a, b) => weekOrder.indexOf(a.day) - weekOrder.indexOf(b.day)
  );

  // Group consecutive days in week order with the same open/close times
  const groups: { days: string[]; openTime: string; closeTime: string }[] = [];
  let currentGroup = null;
  let prevDayIndex = null;

  for (let i = 0; i < sortedHours.length; i++) {
    const { day, openTime, closeTime } = sortedHours[i];
    const dayIndex = weekOrder.indexOf(day);
    if (
      !currentGroup ||
      currentGroup.openTime !== openTime ||
      currentGroup.closeTime !== closeTime ||
      prevDayIndex === null ||
      dayIndex !== prevDayIndex + 1 // not consecutive in week
    ) {
      if (currentGroup) groups.push(currentGroup);
      currentGroup = { days: [day], openTime, closeTime };
    } else {
      currentGroup.days.push(day);
    }
    prevDayIndex = dayIndex;
  }
  if (currentGroup) groups.push(currentGroup);

  // Format each group as a range, using abbreviations if available
  const formatted = groups.map((group) => {
    const { days, openTime, closeTime } = group;
    if (openTime === '' || closeTime === '') {
      return '';
    }
    const abbrDays = days.map((d) => weekAbbr[d as keyof typeof weekAbbr] || d);
    if (abbrDays.length === 1) {
      return `${abbrDays[0]}, ${openTime} to ${closeTime}`;
    } else {
      return `${abbrDays[0]} - ${abbrDays[abbrDays.length - 1]}, ${openTime} to ${closeTime}`;
    }
  });

  return formatted;
}

const OfficeHours = () => {
  const isEditMode = useIsEditing();
  const { page } = useSitecore();
  const pageData = page?.layout?.sitecore?.route?.fields as HoursProps;

  const officeHours = (item: HoursItem) => {
    const labelElement =
      (item?.fields?.heading && item?.fields?.heading?.value !== '') || isEditMode ? (
        <h3 className="text-24 md:text-26 font-centuryGothic font-bold leading-[29px] md:leading-[32px] text-gray-900">
          <PlainTextWrapper field={item?.fields?.heading} />
        </h3>
      ) : (
        <></>
      );
    const disclaimerElement =
      (item?.fields?.disclaimerLabel && item?.fields?.disclaimerLabel?.value !== '') ||
      isEditMode ? (
        <p className="text-18 leading-[29px] font-normal text-gray-900">
          <PlainTextWrapper field={item?.fields?.disclaimerLabel} />
        </p>
      ) : (
        <></>
      );
    const officeHours = item?.fields?.availableHours
      ?.map((oh: OfficeHoursItem) => {
        const day = (oh?.fields?.officeHourType as Data.Lookups.OfficeHoursType)?.fields
          ?.dayofWeekAbbr?.value;
        if (!day) {
          return '';
        }
        return {
          name: oh?.name,
          closeTime: convert24hToAmPm(oh?.fields?.close?.value) || '',
          openTime: convert24hToAmPm(oh?.fields?.open?.value) || '',
          day: day,
        };
      })
      ?.filter((h) => h && h?.day);

    // Type assertion is safe due to the check above
    const linear = getLinearOfficeHours(
      officeHours as { name?: string; day: string; openTime: string; closeTime: string }[]
    );
    if (linear && !isEditMode) {
      return (
        <>
          {labelElement}
          <div className="text-18 leading-[29px] font-normal text-gray-900">
            {linear.map((l, index) => (
              <span className="block" key={index}>
                {l}
              </span>
            ))}
          </div>
          {disclaimerElement}
        </>
      );
    }
    // fallback: list each day
    // Ensure officeHours is an array before mapping to avoid runtime and type errors
    if (officeHours?.length === 0 && !isEditMode) {
      return null;
    }
    return Array.isArray(officeHours) ? (
      <>
        {labelElement}
        <ul className="list-none">
          {!isEditMode
            ? officeHours.map(
                (
                  h: { name: string; day: string; openTime: string; closeTime: string },
                  index: number
                ) =>
                  h?.day &&
                  h?.openTime !== '' &&
                  h?.closeTime !== '' && (
                    <li
                      key={`${h?.day}-${index}`}
                      className="text-18 leading-[29px] font-normal text-gray-900"
                    >
                      {h?.day}: {h?.openTime} - {h?.closeTime}
                    </li>
                  )
              )
            : item?.fields?.availableHours?.map((h: OfficeHoursItem, index: number) => (
                <li key={index} className="text-18 leading-[29px] font-normal text-gray-900">
                  <PlainTextWrapper
                    field={
                      (h?.fields?.officeHourType as Data.Lookups.OfficeHoursType)?.fields
                        ?.dayofWeekAbbr
                    }
                  />
                  : <PlainTextWrapper field={h?.fields?.open} /> -{' '}
                  <PlainTextWrapper field={h?.fields?.close} />
                </li>
              ))}
        </ul>
        {disclaimerElement}
      </>
    ) : null;
  };

  return (
    <section className="component theme-white">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-12">
        {((Array.isArray(pageData?.officeHours) && pageData.officeHours.length > 0) ||
          isEditMode) && (
          <div className="col-span-1">
            {officeHours({
              fields: {
                heading: pageData?.officeHoursTitle || { value: '' },
                disclaimerLabel: pageData?.officeHoursDisclaimerText || { value: '' },
                availableHours: (pageData?.officeHours as Array<OfficeHoursItem>) || [],
              },
            })}
          </div>
        )}

        {((Array.isArray(pageData?.phoneHours) && pageData.phoneHours.length > 0) ||
          isEditMode) && (
          <div className="col-span-1">
            {officeHours({
              fields: {
                heading: pageData?.phoneHoursTitle || { value: '' },
                disclaimerLabel: pageData?.phoneHoursDisclaimerText || { value: '' },
                availableHours: (pageData?.phoneHours as Array<OfficeHoursItem>) || [],
              },
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export const Default = withStandardComponentWrapper(OfficeHours);
